import { escapeHTML } from '../../utils/escape.js';
import { formatElapsed, jornadaAbiertaExcedida } from '../../shared/timesheet-policy.js';

/**
 * Grilla de empleados del kiosco (time-clock-kiosk spec "Kiosk Employee
 * Grid" + "Presence Board"). Render puro: recibe `{ empleados }` — la
 * respuesta CRUDA de `GET /api/kiosco`, ya filtrada a activos por la RPC
 * `kiosk_presencia()` (design.md AD3) — más un ui-state loading/error/ready.
 * NO vuelve a filtrar: solo muestra lo que recibe; ningún otro campo (valor,
 * pin) llega nunca acá, la proyección fija de la RPC los hace inalcanzables.
 *
 * Kiosk UX follow-up (2026-09-02): a turno abierto (`trabajando`) también
 * muestra el tiempo trabajado (`formatElapsed`, contra `desde` = el
 * `entrada_at` del turno abierto que la RPC ya expone) y un punto que
 * "late" (heartbeat) vía CSS (`styles.src.css`). El texto de tiempo lleva
 * `data-elapsed-id` para que `js/kiosco.js` lo refresque cada 1s in-place
 * (`textContent`), sin volver a llamar a este render — mismo motivo que el
 * poll no repinta con el diálogo de PIN abierto: no tocar el mount entero.
 *
 * Kiosk UX round 2 (2026-09-02, owner requests 1 + 3): tarjeta compacta para
 * tablet (`w-14 h-14`/`p-3`/`text-xs`, más columnas por fila) + intención
 * explícita — cada tarjeta muestra "Fichar entrada" o "Fichar salida" según
 * `trabajando`, en vez de un toggle silencioso. `js/kiosco.js` lee ese mismo
 * `trabajando` para decidir el `accion` que abre en el diálogo de PIN
 * (`'entrada'`/`'salida'`) y lo manda al servidor, que vuelve a chequear el
 * estado real antes de escribir (design.md "Kiosk endpoint", 409
 * `estado_cambiado`) — este botón es solo la intención del cliente, nunca la
 * fuente de verdad.
 *
 * F-B (owner decision 2026-09-27): a turno abierto hace más de 12 h la
 * tarjeta suma "¿Te olvidaste de fichar la salida? Avisale a la
 * administración." — calculado acá, en el cliente, con el MISMO `desde` que
 * ya usa el contador (ningún dato nuevo llega al kiosco). El nodo se pinta
 * siempre que `trabajando`, con `hidden` mientras no pase el umbral, para
 * que el tick de 1 s de `js/kiosco.js` lo prenda in-place (`data-olvido-id`)
 * justo al cruzar las 12 h, sin esperar al poll.
 */

/** Aviso de salida olvidada (F-B). */
const AVISO_SALIDA_OLVIDADA = '¿Te olvidaste de fichar la salida? Avisale a la administración.';

function renderEmpleado({ id, nombre, foto_url, trabajando, desde }, nowMs) {
    const safeId = escapeHTML(id);
    const avatar = foto_url
        ? `<img src="${escapeHTML(foto_url)}" alt="" class="w-14 h-14 rounded-2xl object-cover mx-auto" />`
        : `<span class="w-14 h-14 rounded-2xl bg-black/5 dark:bg-white/10 flex items-center justify-center mx-auto"><i class="fa-solid fa-user text-lg text-text-muted" aria-hidden="true"></i></span>`;
    const elapsed = trabajando ? formatElapsed(desde, nowMs) : '';
    const badge = trabajando
        ? `<span class="mt-1 flex flex-col items-center gap-0.5 text-[10px] font-black uppercase tracking-widest text-brand">
            <span class="inline-flex items-center gap-1.5 whitespace-nowrap">
                <span class="heartbeat-dot w-2 h-2 rounded-full bg-brand" role="img" aria-label="Trabajando ahora"></span>
                Trabajando
            </span>
            <span data-elapsed-id="${safeId}" class="normal-case tracking-normal tabular-nums whitespace-nowrap text-text-muted">${escapeHTML(elapsed)}</span>
            <span data-olvido-id="${safeId}" class="normal-case tracking-normal font-bold text-center text-amber-700 dark:text-amber-300"${jornadaAbiertaExcedida(desde, nowMs) ? '' : ' hidden'}>${AVISO_SALIDA_OLVIDADA}</span>
        </span>`
        : '';
    const ctaLabel = trabajando ? 'Fichar salida' : 'Fichar entrada';

    return `
        <button type="button" data-action="kiosco-empleado-seleccionar" data-id="${safeId}"
            class="flex flex-col items-center gap-1 p-3 min-w-0 overflow-hidden rounded-2xl border border-border-subtle bg-surface active:scale-95 transition-transform">
            ${avatar}
            <p class="text-xs font-bold text-text-primary text-center">${escapeHTML(nombre)}</p>
            ${badge}
            <span class="mt-1 text-[10px] font-black uppercase tracking-widest text-brand">${ctaLabel}</span>
        </button>
    `;
}

/**
 * @param {{ empleados: Array<object> }|null} viewModel usado solo en `status: 'ready'`.
 * @param {{ status: 'loading'|'error'|'ready' }} [uiState]
 * @param {number} [nowMs] client clock para `formatElapsed`; default `Date.now()`.
 */
export function KioscoGrid(viewModel, uiState = {}, nowMs = Date.now()) {
    if (uiState.status === 'loading') {
        return `
            <div class="flex flex-col items-center justify-center py-24 text-center" role="status" aria-live="polite">
                <i class="fa-solid fa-circle-notch fa-spin text-3xl text-brand mb-4" aria-hidden="true"></i>
                <p class="text-xs font-black text-text-muted uppercase tracking-widest">Cargando...</p>
            </div>
        `;
    }

    if (uiState.status === 'error') {
        return `
            <div class="flex flex-col items-center justify-center py-24 text-center" role="alert">
                <p class="text-sm font-bold text-text-primary mb-6">No pudimos cargar el kiosco. Probá de nuevo.</p>
                <button data-action="kiosco-retry" class="px-6 py-3 rounded-2xl bg-brand text-white text-xs font-black uppercase tracking-widest shadow-lg">Reintentar</button>
            </div>
        `;
    }

    const empleados = viewModel?.empleados ?? [];
    const gridHtml = empleados.length > 0
        ? empleados.map((empleado) => renderEmpleado(empleado, nowMs)).join('')
        : '<p class="col-span-full text-center text-xs text-text-muted py-8">No hay empleados para fichar.</p>';

    return `
        <section aria-label="Kiosco de fichadas" class="max-w-3xl mx-auto px-6 py-10">
            <h1 class="text-xl font-black uppercase tracking-tight text-text-primary text-center mb-8">Fichar entrada / salida</h1>
            <div class="grid grid-cols-[repeat(auto-fill,minmax(8.5rem,1fr))] gap-3">${gridHtml}</div>
        </section>
    `;
}
